/**
 * Sentry bootstrap + PII scrubber for the frontend.
 *
 * Introduced for issue #681 (Ultra Review 2026-04-18).
 *
 * The `beforeSend` hook **allowlists** the event before it ships.
 * Anything that could be PII (emails, user IDs, shop names, query
 * strings, request bodies) is redacted. Exception type, message, and
 * stack frames are kept — those are not PII in this codebase.
 *
 * The initialisation is **opt-in**: when
 * `process.env.REACT_APP_SENTRY_DSN` is blank, this module is a no-op
 * and the app runs exactly as before. That keeps local dev and tests
 * off the wire.
 *
 * Env vars (all `REACT_APP_*`-prefixed so CRA picks them up at build
 * time):
 *
 *  - `REACT_APP_SENTRY_DSN`         — enables Sentry when set
 *  - `REACT_APP_SENTRY_ENVIRONMENT` — staging / production (falls back
 *                                     to `NODE_ENV`)
 *  - `REACT_APP_SENTRY_RELEASE`     — release tag (e.g. git sha); ties
 *                                     errors to a Vercel deploy
 */

// Keep scrubber logic out of the dynamic import so it's unit-testable
// without loading @sentry/react.

const REDACTED = '[redacted]';

function scrubUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const q = url.indexOf('?');
  const f = url.indexOf('#');
  let end = url.length;
  if (q !== -1) end = Math.min(end, q);
  if (f !== -1) end = Math.min(end, f);
  return url.slice(0, end);
}

function scrubRequest(request) {
  if (!request || typeof request !== 'object') return request;
  const out = {};
  // Only these keys are allowed through.
  if (request.method) out.method = request.method;
  if (request.url) out.url = scrubUrl(request.url);
  // headers / cookies / data are intentionally dropped.
  return out;
}

function scrubUser() {
  // Never tie an event to a specific end-user (email / id / ip_address).
  return {};
}

function scrubExtra(extra) {
  if (!extra || typeof extra !== 'object') return {};
  // Only keys namespaced with `ob_` are allowed. Anyone adding diagnostic
  // context must opt in per-key.
  const out = {};
  for (const [k, v] of Object.entries(extra)) {
    if (k.startsWith('ob_')) out[k] = v;
  }
  return out;
}

function scrubBreadcrumb(crumb) {
  const data = crumb?.data;
  if (data && typeof data === 'object') {
    const newData = { ...data };
    if (typeof newData.url === 'string') newData.url = scrubUrl(newData.url);
    for (const k of ['request_body', 'response_body', 'body']) {
      if (k in newData) newData[k] = REDACTED;
    }
    return { ...crumb, data: newData };
  }
  return crumb;
}

/**
 * Sentry `beforeSend` hook. Exported for unit testing; not a public API.
 */
export function beforeSend(event) {
  if (!event) return event;
  event.request = scrubRequest(event.request);
  event.user = scrubUser();
  event.extra = scrubExtra(event.extra);
  if (event.breadcrumbs?.values && Array.isArray(event.breadcrumbs.values)) {
    event.breadcrumbs = {
      values: event.breadcrumbs.values.map(scrubBreadcrumb),
    };
  }
  return event;
}

export const __scrubbers = {
  scrubUrl,
  scrubRequest,
  scrubUser,
  scrubExtra,
  scrubBreadcrumb,
};

/**
 * Initialise Sentry if a DSN is configured. Returns true if Sentry was
 * loaded and init'd, false otherwise. Safe to call multiple times —
 * `@sentry/react`'s init is idempotent in practice.
 */
export async function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Silent no-op — don't log at INFO because this is the common dev case.
    return false;
  }

  let Sentry;
  try {
    Sentry = await import('@sentry/react');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      'REACT_APP_SENTRY_DSN is set but @sentry/react is not installed.',
      err
    );
    return false;
  }

  Sentry.init({
    dsn,
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.NODE_ENV ||
      'unknown',
    release: process.env.REACT_APP_SENTRY_RELEASE,
    // Send the minimum that still identifies an error. PII redaction is
    // enforced in beforeSend; these flags are defense in depth.
    sendDefaultPii: false,
    attachStacktrace: true,
    beforeSend,
    // Tracing is off by default. Turn it on per-environment via
    // REACT_APP_SENTRY_TRACES_SAMPLE_RATE if you want flamegraphs.
    tracesSampleRate: Number(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0
    ),
  });

  return true;
}
